import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { Shield, Lock, CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "@/api";

const StaffSecuritySettings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [setupMode, setSetupMode] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await api.staff2faStatus();
      setTwoFactorEnabled(data.twoFactorEnabled);
    } catch (e) {
      toast({
        title: t('staff.security.loadErrorTitle'),
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      const data = await api.staff2faSetup();
      
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setSetupMode(true);
      
      toast({
        title: t('staff.security.setupStarted'),
        description: t('staff.security.scanQrDesc'),
      });
    } catch (e) {
      toast({
        title: t('staff.security.error'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode) {
      toast({
        title: t('staff.security.error'),
        description: t('staff.security.enterCode'),
        variant: "destructive",
      });
      return;
    }

    try {
      await api.staff2faEnable(verificationCode);
      
      setTwoFactorEnabled(true);
      setSetupMode(false);
      setQrCode(null);
      setSecret(null);
      setVerificationCode("");
      
      toast({
        title: t('staff.security.enabledTitle'),
        description: t('staff.security.enabledDesc'),
      });
    } catch (e) {
      toast({
        title: t('staff.security.error'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  const handleDisable2FA = async () => {
    if (!verificationCode) {
      toast({
        title: t('staff.security.error'),
        description: t('staff.security.enterCode'),
        variant: "destructive",
      });
      return;
    }

    try {
      await api.staff2faDisable(verificationCode);
      
      setTwoFactorEnabled(false);
      setVerificationCode("");
      
      toast({
        title: t('staff.security.disabledTitle'),
        description: t('staff.security.disabledDesc'),
      });
    } catch (e) {
      toast({
        title: t('staff.security.error'),
        description: e.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-background rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('staff.security.title')}
            </h1>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/staff/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('staff.security.backToDashboard')}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {t('staff.security.twoFaTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">{t('staff.security.twoFaStatus')}</div>
                <div className="text-sm text-muted-foreground">
                  {twoFactorEnabled
                    ? t('staff.security.twoFaEnabled')
                    : t('staff.security.twoFaDisabled')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {twoFactorEnabled ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-border" />
                )}
              </div>
            </div>

            {!twoFactorEnabled && !setupMode && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('staff.security.enableHelp')}
                </p>
                <Button onClick={handleSetup2FA}>
                  {t('staff.security.setup2fa')}
                </Button>
              </div>
            )}

            {setupMode && (
              <div className="space-y-4">
                <div className="p-4 border rounded-lg bg-blue-50">
                  <h3 className="font-medium mb-2">{t('staff.security.step1Title')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('staff.security.step1Desc')}
                  </p>
                  {qrCode && (
                    <div className="flex justify-center">
                      <img src={qrCode} alt={t('staff.security.qrAlt')} className="border p-2 bg-background rounded" />
                    </div>
                  )}
                  {secret && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">
                        {t('staff.security.manualCode')}
                      </p>
                      <code className="block p-2 bg-background border rounded text-sm font-mono">
                        {secret}
                      </code>
                    </div>
                  )}
                </div>

                <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2">{t('staff.security.step2Title')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('staff.security.step2Desc')}
                  </p>
                  <input
                    type="text"
                    placeholder={t('staff.security.codePlaceholder')}
                    maxLength="6"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full px-3 py-2 border rounded-md mb-4"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleEnable2FA}>
                      {t('staff.security.enable2fa')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSetupMode(false);
                        setQrCode(null);
                        setSecret(null);
                        setVerificationCode("");
                      }}
                    >
                      {t('staff.security.cancel')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {twoFactorEnabled && (
              <div className="space-y-4">
                <div className="p-4 border rounded-lg bg-green-50">
                  <p className="text-sm text-green-800">
                    ✓ {t('staff.security.alreadyEnabled')}
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2 text-red-600">{t('staff.security.disableTitle')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('staff.security.disableDesc')}
                  </p>
                  <input
                    type="text"
                    placeholder={t('staff.security.codePlaceholder')}
                    maxLength="6"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full px-3 py-2 border rounded-md mb-4"
                  />
                  <Button variant="destructive" onClick={handleDisable2FA}>
                    {t('staff.security.disable2fa')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StaffSecuritySettings;
